import { useCallback, useEffect, useRef } from 'react';
import api from '../../services/api';

interface SessionManagerConfig {
  sessionTimeout: number;
  warningThreshold: number;
  activityUpdateThrottle: number;
}

const DEFAULT_CONFIG: SessionManagerConfig = {
  sessionTimeout: 60 * 60 * 1000, // 1 hour
  warningThreshold: 5 * 60 * 1000, // 5 minutes
  activityUpdateThrottle: 30000, // 30 seconds
};

const MAX_SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours max

interface SessionManagerCallbacks {
  onSessionExpired?: () => void;
  onShowWarning?: () => void;
  onHideWarning?: () => void;
  onSessionExtended?: () => void;
}

export const useSessionManager = (
  isAuthenticated: boolean,
  callbacks: SessionManagerCallbacks = {},
  config: Partial<SessionManagerConfig> = {}
) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  
  // Refs for state management
  const lastActivityUpdate = useRef<number>(0);
  const sessionExpiryRef = useRef<number>(0);
  const warningShownRef = useRef<boolean>(false);
  const isOnlineRef = useRef<boolean>(navigator.onLine);

  // Reset session timer on user activity
  const resetSessionTimer = useCallback(() => {
    if (!isAuthenticated) return;

    const now = Date.now();
    if (now - lastActivityUpdate.current < finalConfig.activityUpdateThrottle) return;

    lastActivityUpdate.current = now;
    sessionExpiryRef.current = now + finalConfig.sessionTimeout;
    
    // Hide warning if it was shown
    if (warningShownRef.current) {
      warningShownRef.current = false;
      callbacks.onHideWarning?.();
    }
  }, [isAuthenticated, finalConfig.activityUpdateThrottle, finalConfig.sessionTimeout, callbacks]);

  // Extend session via API
  const extendSession = useCallback(async () => {
    if (!isOnlineRef.current) return;

    const now = Date.now();
    const currentSessionEnd = sessionExpiryRef.current;
    const proposedNewEnd = now + finalConfig.sessionTimeout;
    
    // Check if extending would exceed max session duration
    if (proposedNewEnd - (currentSessionEnd - finalConfig.sessionTimeout) > MAX_SESSION_DURATION) {
      console.warn('Session extension would exceed maximum duration');
      return;
    }

    try {
      const response = await api.post('/users/extend-session');
      if (response.data.status === 'success') {
        sessionExpiryRef.current = proposedNewEnd;
        
        if (warningShownRef.current) {
          warningShownRef.current = false;
          callbacks.onHideWarning?.();
        }
        
        callbacks.onSessionExtended?.();
      }
    } catch (error) {
      console.error('Session extension failed:', error);
    }
  }, [finalConfig.sessionTimeout, callbacks]);

  // Check session status
  const checkSessionStatus = useCallback(() => {
    if (!isAuthenticated) return;

    const now = Date.now();
    const timeLeft = sessionExpiryRef.current - now;
    
    if (timeLeft <= 0) {
      callbacks.onSessionExpired?.();
    } else if (timeLeft <= finalConfig.warningThreshold && !warningShownRef.current) {
      warningShownRef.current = true;
      callbacks.onShowWarning?.();
    }
  }, [isAuthenticated, finalConfig.warningThreshold, callbacks]);

  // Handle user activity with debouncing
  useEffect(() => {
    if (!isAuthenticated) return;

    let activityTimeout: NodeJS.Timeout;
    
    const debouncedActivityHandler = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(resetSessionTimer, 100);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, debouncedActivityHandler);
    });

    return () => {
      clearTimeout(activityTimeout);
      events.forEach(event => {
        window.removeEventListener(event, debouncedActivityHandler);
      });
    };
  }, [isAuthenticated, resetSessionTimer]);

  // Set up session status check interval
  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(checkSessionStatus, 30000); // Check every 30 seconds
    return () => clearInterval(intervalId);
  }, [isAuthenticated, checkSessionStatus]);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => { isOnlineRef.current = true; };
    const handleOffline = () => { isOnlineRef.current = false; };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize session when authentication changes
  useEffect(() => {
    if (isAuthenticated) {
      const now = Date.now();
      sessionExpiryRef.current = now + finalConfig.sessionTimeout;
      warningShownRef.current = false;
      resetSessionTimer();
    } else {
      sessionExpiryRef.current = 0;
      warningShownRef.current = false;
    }
  }, [isAuthenticated, finalConfig.sessionTimeout, resetSessionTimer]);

  return {
    resetSessionTimer,
    extendSession,
    checkSessionStatus,
  };
}; 